import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiStar, FiEdit2 } from 'react-icons/fi';
import { supabase } from '../config/supabase';
import RatingModal from './RatingModal';
import { useOrderItemRating } from '../hooks/useProductRatings';

const ItemCard = styled.div`
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  background: white;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled(Link)`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;

  &:hover {
    color: #5efba9;
  }
`;

const ItemMeta = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const ItemPrice = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-top: 8px;
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`;

const VariantInfo = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const RatingButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #5efba9;
  background: ${props => props.hasRating ? '#d1fae5' : 'white'};
  color: ${props => props.hasRating ? '#065f46' : '#1f2937'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.hasRating ? '#a7f3d0' : '#f0fdf4'};
  }
`;

const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #6b7280;
`;

const EmptyImage = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

export default function OrderItemCard({ orderItem, order }) {
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [variantOptions, setVariantOptions] = useState([]);
  const { rating, loading: ratingLoading } = useOrderItemRating(orderItem?.id);

  // Fetch variant options if variant_id exists
  useEffect(() => {
    const fetchVariantOptions = async () => {
      if (!orderItem?.variant_id) {
        setVariantOptions([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('product_variants')
          .select(`
            id,
            sku,
            variant_options:product_variant_options(
              option_group:product_option_groups(
                id,
                name,
                label
              ),
              option_value:product_option_values(
                id,
                value
              )
            )
          `)
          .eq('id', orderItem.variant_id)
          .single();

        if (error) {
          console.error('Error fetching variant options:', error);
          return;
        }

        if (data?.variant_options) {
          setVariantOptions(data.variant_options.map(vo => ({
            groupLabel: vo.option_group?.label || vo.option_group?.name || 'Option',
            value: vo.option_value?.value || 'Unknown'
          })));
        }
      } catch (err) {
        console.error('Error fetching variant options:', err);
      }
    };

    fetchVariantOptions();
  }, [orderItem?.variant_id]);

  if (!orderItem) return null;

  const productImage = orderItem.product?.images?.[0] || orderItem.product?.img || null;
  const productName = orderItem.product_name || orderItem.product?.name || 'Unknown Product';
  const unitPrice = orderItem.unit_price || orderItem.price || 0;
  const quantity = orderItem.quantity || 1;
  const total = unitPrice * quantity;
  const currency = order?.currency || 'OMR';
  const productId = orderItem.product_id;

  // Check if order status is 'delivered' to allow rating
  const canRate = order?.status === 'delivered';

  // Get variant info if available
  const variantInfo = variantOptions.length > 0
    ? variantOptions.map(vo => `${vo.groupLabel}: ${vo.value}`).join(', ')
    : orderItem.variant_id ? `Variant: ${orderItem.sku || orderItem.variant_id}` : null;

  return (
    <>
      <ItemCard>
        <ItemHeader>
          {productImage ? (
            <ItemImage src={productImage} alt={productName} />
          ) : (
            <EmptyImage>📦</EmptyImage>
          )}
          <ItemDetails>
            <ItemName to={`/product/${productId}`}>
              {productName}
            </ItemName>
            {variantInfo && (
              <VariantInfo>{variantInfo}</VariantInfo>
            )}
            <ItemMeta>
              Qty: {quantity} × {unitPrice.toFixed(2)} {currency}
            </ItemMeta>
            {orderItem.note && (
              <ItemMeta style={{ marginTop: '4px', fontStyle: 'italic', color: '#4b5563' }}>
                Note: {orderItem.note}
              </ItemMeta>
            )}
            <ItemPrice>
              {total.toFixed(2)} {currency}
            </ItemPrice>
          </ItemDetails>
        </ItemHeader>
        <ItemFooter>
          <div>
            {rating && (
              <RatingDisplay>
                <FiStar style={{ fill: '#fbbf24', color: '#fbbf24' }} />
                <span>{rating.stars}/5</span>
                {rating.body && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#9ca3af' }}>
                    "{rating.body.substring(0, 50)}{rating.body.length > 50 ? '...' : ''}"
                  </span>
                )}
              </RatingDisplay>
            )}
          </div>
          {canRate && (
            <RatingButton
              hasRating={!!rating}
              onClick={() => setShowRatingModal(true)}
            >
              {rating ? (
                <>
                  <FiEdit2 /> Edit Review
                </>
              ) : (
                <>
                  <FiStar /> Rate Product
                </>
              )}
            </RatingButton>
          )}
        </ItemFooter>
      </ItemCard>

      {showRatingModal && (
        <RatingModal
          orderItem={orderItem}
          order={order}
          existingRating={rating}
          onClose={() => setShowRatingModal(false)}
          onSubmitted={() => {
            setShowRatingModal(false);
            // Rating will refresh automatically via hook
            window.location.reload(); // Quick refresh to update UI
          }}
        />
      )}
    </>
  );
}

